/**
 * Auth context — manages JWT token state, login/signup/logout, and token refresh.
 *
 * SECURITY NOTE (MVP tradeoff): In real mode, JWTs are stored in localStorage.
 * For production hardening, migrate to httpOnly cookies managed by the gateway.
 */

import { config } from '@/config/environment';
import { endChatSession, startChatSession } from '@/hooks/chat/chatSessionStorage';
import { createQueryClient } from '@/lib/queryClient';
import type {
  AuthUser,
  ChangePasswordRequest,
  LoginRequest,
  LoginResponse,
  SignupRequest,
} from '@/services/authService';
import * as authService from '@/services/authService';
import { setPipelineTenantOverride } from '@/services/pipelineTenant';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  TOKEN_KEY,
  USER_KEY,
  clearTrustToken,
  clearTrustTokenOnSignOut,
  decodeJwtPayload,
  findTrustTokenForEmail,
  getTrustToken,
  isTokenExpired,
  registerTokenGetter,
  setTrustToken,
  userFromJwt,
} from './authTokenManager';

// Re-export for backward compatibility (api.ts imports from here)
export { getAuthToken, registerTokenGetter } from './authTokenManager';

// ---------------------------------------------------------------------------
// Context types
// ---------------------------------------------------------------------------

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  devTokenError: string | null;
}

interface AuthContextValue extends AuthState {
  /** Rejects with the gateway's error; see ``authService.loginChallenge`` for the two-step asks. */
  login: (req: LoginRequest) => Promise<void>;
  /** Adopt a session issued outside ``login`` (two-step enrollment on an enrollment token). */
  startSession: (resp: LoginResponse) => void;
  signup: (req: SignupRequest) => Promise<void>;
  logout: () => void;
  changePassword: (req: ChangePasswordRequest) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Token helpers — the token is the authority on who is signed in
// ---------------------------------------------------------------------------

/** The user a token was issued to (its ``sub``), or undefined. */
function tokenUserId(token: string | null | undefined): string | undefined {
  return (token && userFromJwt(token)?.user_id) || undefined;
}

/** Seconds a token has left, or null when it carries no expiry. */
function secondsLeft(token: string): number | null {
  const payload = decodeJwtPayload(token);
  return payload && typeof payload.exp === 'number' ? payload.exp - Date.now() / 1000 : null;
}

/**
 * The same user's newer, unexpired token another tab of this browser saved
 * (it refreshed first), or null. Adopting it instead of signing out keeps one
 * tab's failed refresh from removing the shared token and signing every other
 * tab of that user out (CASA 6.6.1).
 */
function sameUserSavedToken(current: string): string | null {
  const saved = localStorage.getItem(TOKEN_KEY);
  if (!saved || saved === current || isTokenExpired(saved)) return null;
  const mine = tokenUserId(current);
  return mine && tokenUserId(saved) === mine ? saved : null;
}

/**
 * Save a renewed token of this tab's session (a refresh, a password change) —
 * unless the browser's saved session is now someone else's: another person
 * signed in in another tab, and overwriting would sign them out. A sign-in
 * (startSession) is the one write that takes the saved session over.
 */
function saveRenewedToken(token: string, user?: AuthUser | null): void {
  const saved = localStorage.getItem(TOKEN_KEY);
  if (saved !== null && tokenUserId(saved) !== tokenUserId(token)) return;
  localStorage.setItem(TOKEN_KEY, token);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/** The user to restore for ``token``: its claims, or USER_KEY when it names the same user. */
function restoredUserFor(token: string): AuthUser | null {
  const tokenUser = userFromJwt(token);
  try {
    const raw = localStorage.getItem(USER_KEY);
    const parsed: AuthUser | null = raw ? JSON.parse(raw) : null;
    // The token says who is signed in; a USER_KEY naming someone else
    // (written by another session) is not believed.
    if (parsed && (!tokenUser?.user_id || parsed.user_id === tokenUser.user_id)) return parsed;
  } catch {
    /* unreadable USER_KEY: the token's claims will do */
  }
  return tokenUser;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [devTokenError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tokenRef = useRef<string | null>(null);

  const isDevMode = config.AUTH_MODE === 'dev';

  // One react-query client and one mounted tree per session (CASA 6.6.1).
  // Query keys are mostly not viewer-scoped, so a cache that outlived its
  // session would render the previous user's answers to the next person to
  // sign in here. A session change clears the old client and swaps in a new
  // one, and the tree below remounts on the new epoch, so nothing mounted keeps
  // a handle on the old client. Work of the old session that lands late (a
  // mutation's onSuccess, a query) writes into a client nobody reads any more.
  const [session, setSession] = useState(() => ({ epoch: 0, queryClient: createQueryClient() }));
  const sessionRef = useRef(session);
  const renewSession = useCallback(() => {
    sessionRef.current.queryClient.clear(); // queries and mutations
    const next = { epoch: sessionRef.current.epoch + 1, queryClient: createQueryClient() };
    sessionRef.current = next;
    setSession(next);
  }, []);

  // Forget the session and everything per-user this tab holds for it (CASA
  // 6.6.1). Every sign-out, chosen or forced, goes through here, BEFORE any
  // redirect: the query cache (renewSession), the chat's in-flight work and its
  // sessionStorage (endChatSession), the Pipeline override. The saved token
  // (shared by every tab of this browser) is removed only while it is this
  // session's user's — another person's saved session (they signed in in
  // another tab) is not ours to remove; this user's own, even one a late
  // refresh wrote back, is. ``owner`` names the user when tokenRef does not
  // hold them yet (a page load).
  // Kept: UI preferences (chat model, panel open state) and the per-account
  // trusted-browser tokens: a session that ends on its own (it expired, its
  // refresh failed) keeps them, so everyday sign-in stays password-only.
  // Only an explicit Sign out (``logout``) removes one: the signing-out
  // account's.
  const forgetSession = useCallback(
    (opts: { owner?: string } = {}) => {
      const owner = opts.owner ?? tokenUserId(tokenRef.current);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      tokenRef.current = null; // signed out now, not at the next render
      setToken(null);
      setUser(null);
      const saved = localStorage.getItem(TOKEN_KEY);
      if (saved === null || tokenUserId(saved) === owner) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
      }
      setPipelineTenantOverride(null); // a sign-out never carries a Pipeline override into the next session
      endChatSession();
      renewSession();
    },
    [renewSession],
  );

  // Keep tokenRef in sync — this runs during render (before effects),
  // so the getter is always current even for child-first effect ordering.
  tokenRef.current = token;

  // Register the ref-based token getter once (stable across all renders)
  useEffect(() => {
    registerTokenGetter(() => tokenRef.current);
  }, []);

  // The refresh the timer runs; assigned below (it needs scheduleRefresh).
  const refreshRef = useRef<(currentToken: string) => Promise<void>>(async () => {});

  // Schedule token refresh (real mode only)
  const scheduleRefresh = useCallback(
    (currentToken: string, expiresIn: number) => {
      if (isDevMode) return;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

      // Refresh 60 seconds before expiry
      const delayMs = Math.max((expiresIn - 60) * 1000, 5000);
      refreshTimerRef.current = setTimeout(() => {
        void refreshRef.current(currentToken);
      }, delayMs);
    },
    [isDevMode],
  );

  // Take over a token of this same session's user (another tab's refresh).
  const adoptToken = useCallback(
    (next: string) => {
      tokenRef.current = next;
      setToken(next);
      const left = secondsLeft(next);
      if (left !== null) scheduleRefresh(next, left);
    },
    [scheduleRefresh],
  );

  // Refresh ``currentToken``. The outcome applies only while it is still this
  // tab's token: a refresh in flight when the session ended or moved on (a
  // sign-out, another sign-in, an adopted token) lands on nothing (CASA 6.6.1).
  const refreshSession = useCallback(
    async (currentToken: string) => {
      let resp: Awaited<ReturnType<typeof authService.refreshToken>>;
      try {
        resp = await authService.refreshToken(currentToken);
      } catch {
        if (tokenRef.current !== currentToken) return;
        // Another tab of this user may have refreshed first: carry on with its token.
        const newer = sameUserSavedToken(currentToken);
        if (newer) {
          adoptToken(newer);
          return;
        }
        // Refresh failed — force full logout + redirect
        forgetSession();
        // Hard redirect — ProtectedRoute may not re-render promptly on an idle page
        window.location.href = '/login';
        return;
      }
      if (tokenRef.current !== currentToken) return;
      tokenRef.current = resp.token;
      setToken(resp.token);
      saveRenewedToken(resp.token);
      scheduleRefresh(resp.token, resp.expires_in);
    },
    [adoptToken, forgetSession, scheduleRefresh],
  );
  refreshRef.current = refreshSession;

  // Refresh token when user returns to the tab (handles laptop sleep / tab throttle)
  useEffect(() => {
    if (isDevMode) return;

    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      const current = tokenRef.current;
      if (!current) return;

      const payload = decodeJwtPayload(current);
      if (!payload || typeof payload.exp !== 'number') return;

      const remaining = payload.exp - Date.now() / 1000;
      // If less than 5 minutes left, refresh immediately
      if (remaining < 300 && remaining > 0) {
        void refreshSession(current);
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isDevMode, refreshSession]);

  // Other tabs of this browser (CASA 6.6.1). The browser fires `storage` in
  // every other tab of the origin when the saved token changes:
  // - it now holds a newer token of THIS tab's user: another tab refreshed;
  //   adopt it (this tab's own stale refresh would otherwise fail later);
  // - the token it held was THIS tab's user's and is gone or someone else's
  //   now: that user signed out (or another person signed in) there, so sign
  //   out here too, leaving the other tab's saved session in place;
  // - it was another account's token: not this tab's business;
  // - storage was cleared: whose session it held is unknowable, so sign out.
  // A sign-out here removes the saved token only if it is this user's (so a
  // late refresh's write-back goes too, and another person's stays).
  // A page restored from the back-forward cache missed the events while it was
  // frozen: it reconciles its token with the saved one the same way.
  useEffect(() => {
    if (isDevMode) return;

    /** The saved token went from ``oldValue`` to ``newValue``; ``cleared``: storage.clear(). */
    function reconcile(oldValue: string | null, newValue: string | null, cleared: boolean) {
      const current = tokenRef.current;
      if (!current) return; // signed out here already
      const mine = tokenUserId(current);
      if (!cleared) {
        if (newValue && mine && tokenUserId(newValue) === mine) {
          if (newValue !== current && !isTokenExpired(newValue)) adoptToken(newValue);
          return;
        }
        if (!mine || tokenUserId(oldValue) !== mine) return;
      }
      forgetSession();
      window.location.href = '/login';
    }

    function handleStorage(e: StorageEvent) {
      if (e.storageArea && e.storageArea !== localStorage) return;
      if (e.key !== null && e.key !== TOKEN_KEY) return;
      reconcile(e.oldValue, e.newValue, e.key === null);
    }

    function handlePageShow(e: PageTransitionEvent) {
      if (!e.persisted) return;
      const current = tokenRef.current;
      const saved = localStorage.getItem(TOKEN_KEY);
      if (!current || saved === current) return;
      reconcile(current, saved, false);
    }

    window.addEventListener('storage', handleStorage);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isDevMode, adoptToken, forgetSession]);

  // Cleanup refresh timer
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  // Initialize auth on mount
  useEffect(() => {
    async function init() {
      if (isDevMode) {
        // Generate auth locally — no network call, no gateway dependency.
        const resp = await authService.getLocalDevToken();
        startChatSession(resp.user.user_id, { resume: true });
        setToken(resp.auth.token);
        setUser(resp.user);
      } else {
        // Real mode: restore from localStorage
        const savedToken = localStorage.getItem(TOKEN_KEY);

        // A reload of a saved session: its own chat may rehydrate.
        const restore = (restoredToken: string) => {
          const restoredUser = restoredUserFor(restoredToken);
          startChatSession(restoredUser?.user_id ?? '', { resume: true });
          tokenRef.current = restoredToken;
          setToken(restoredToken);
          setUser(restoredUser);
          const left = secondsLeft(restoredToken);
          if (left !== null) scheduleRefresh(restoredToken, left);
        };

        if (!savedToken) {
          // Nobody is signed in on this load, so the chat this tab still
          // holds (another tab's sign-out never reached it) is nobody's.
          endChatSession();
        } else if (!isTokenExpired(savedToken)) {
          restore(savedToken);
        } else {
          // Token expired — try refresh
          try {
            const resp = await authService.refreshToken(savedToken);
            // The saved session may have changed while we waited: this user
            // signed out, or someone else signed in, in another tab. Then the
            // refreshed token is not ours to use (it would sign the old user
            // back in here, or write them back into storage): stay signed out
            // and leave the saved session as the other tab left it.
            const savedNow = localStorage.getItem(TOKEN_KEY);
            if (savedNow !== savedToken && tokenUserId(savedNow) !== tokenUserId(savedToken)) {
              forgetSession({ owner: tokenUserId(savedToken) });
              // Revoke it only when this user signed out (the sign-out meant
              // it). After someone else's sign-in, logout would end every
              // session of this user everywhere, which that sign-in never does
              // on the non-race path: the unsaved token just expires.
              if (savedNow === null) {
                authService.logout(resp.token).catch(() => {
                  /* it still expires on its own */
                });
              }
              setIsLoading(false);
              return;
            }
            const refreshedUser = userFromJwt(resp.token);
            startChatSession(refreshedUser?.user_id ?? '', { resume: true });
            tokenRef.current = resp.token;
            setToken(resp.token);
            setUser(refreshedUser);
            saveRenewedToken(resp.token, refreshedUser);
            scheduleRefresh(resp.token, resp.expires_in);
          } catch {
            // Another tab of this user may have saved a newer session while we
            // waited: carry on with it. Otherwise the session can no longer be
            // renewed, and what the tab kept (the chat) is the signed-out
            // user's; another person's saved session stays.
            const newer = sameUserSavedToken(savedToken);
            if (newer) restore(newer);
            else forgetSession({ owner: tokenUserId(savedToken) });
          }
        }
      }
      setIsLoading(false);
    }

    init();
  }, [isDevMode, scheduleRefresh, forgetSession]);

  // Every session this tab is handed (login, two-step enrollment, signup; a
  // password change only when it names another user) starts clean: a new
  // query client and tree, no Pipeline override, and none of the chat this tab
  // still holds — a tab that never ran a sign-out (another tab's, a restored
  // browser session) may hold someone else's (CASA 6.6.1). A sign-in takes
  // the browser's saved session over. Pages that call it and then navigate
  // must guard against rendering signed-in (they remount on the new epoch).
  const startSession = useCallback(
    (resp: LoginResponse) => {
      renewSession();
      setPipelineTenantOverride(null);
      startChatSession(resp.user.user_id, { resume: false });
      tokenRef.current = resp.auth.token; // a refresh of the old session in flight now lands on nothing
      setToken(resp.auth.token);
      setUser(resp.user);
      localStorage.setItem(TOKEN_KEY, resp.auth.token);
      localStorage.setItem(USER_KEY, JSON.stringify(resp.user));
      scheduleRefresh(resp.auth.token, resp.auth.expires_in);
    },
    [scheduleRefresh, renewSession],
  );

  // Login. A trusted-browser token saved for this account (C11) rides along
  // so the gateway can waive the code; with none saved the request is exactly
  // what the caller passed.
  const login = useCallback(
    async (req: LoginRequest) => {
      // Without a workspace (XC-TEAM-INVITES T5) the one token saved for this email rides, if any.
      const savedFor = req.tenant_slug
        ? { slug: req.tenant_slug, token: getTrustToken(req.tenant_slug, req.email) }
        : findTrustTokenForEmail(req.email);
      const saved = req.trust_token ?? savedFor?.token ?? null;
      const body: LoginRequest = saved ? { ...req, trust_token: saved } : req;
      let resp: LoginResponse;
      try {
        resp = await authService.login(body);
      } catch (err) {
        // The gateway still asked for a code: the saved token is spent
        // (expired, a password change, a factor reset). Stop sending it.
        if (saved && savedFor && authService.loginChallenge(err)?.kind === 'mfa_required') {
          clearTrustToken(savedFor.slug, req.email);
        }
        throw err;
      }
      // Saved under the workspace that signed in, whichever way it was chosen.
      const signedInSlug = resp.user?.tenant_slug || req.tenant_slug;
      if (resp.mfa_trust_token && signedInSlug) {
        setTrustToken(signedInSlug, req.email, resp.mfa_trust_token);
      }
      startSession(resp);
    },
    [startSession],
  );

  // Signup
  const signup = useCallback(
    async (req: SignupRequest) => {
      startSession(await authService.signup(req));
    },
    [startSession],
  );

  // Change password (CASA 1.1.2) — the gateway returns a fresh login
  const changePassword = useCallback(
    async (req: ChangePasswordRequest) => {
      const current = tokenRef.current;
      if (!current) throw new Error('Not signed in');
      const resp = await authService.changePassword(current, req);
      // Signed out, or someone else signed in, meanwhile: not ours to apply.
      // The same user on a refreshed or adopted token still takes it — the
      // password change supersedes (the server revoked the others).
      const now = tokenRef.current;
      if (!now || tokenUserId(now) !== tokenUserId(current)) return;
      if (resp.user.user_id && resp.user.user_id === tokenUserId(current)) {
        // The same person's session continues on a new token: their cache,
        // chat and mounted pages are theirs, and the page that asked (the
        // forced flow) must finish as it started, not remount mid-submit.
        // Other tabs of this browser adopt it from storage.
        tokenRef.current = resp.auth.token;
        setToken(resp.auth.token);
        setUser(resp.user);
        saveRenewedToken(resp.auth.token, resp.user);
        scheduleRefresh(resp.auth.token, resp.auth.expires_in);
        return;
      }
      startSession(resp); // a session for anyone else starts clean
    },
    [startSession, scheduleRefresh],
  );

  // Logout — the explicit, user-chosen Sign out. Revoke server-side (CASA
  // 2.2.1), end this browser's trust for the account signing out (CASA
  // 6.6.1: the one path that clears it; a session that ends on its own keeps
  // it), then forget locally even if the gateway is unreachable.
  const logout = useCallback(() => {
    const current = tokenRef.current;
    if (current && !isDevMode) {
      authService.logout(current).catch(() => {
        /* the token still expires on its own; nothing more to do here */
      });
    }
    // The account signing out: the session's user, the token's claims filling what it lacks.
    const claims = current ? userFromJwt(current) : null;
    clearTrustTokenOnSignOut(user?.tenant_slug || claims?.tenant_slug, user?.email || claims?.email);
    forgetSession();
  }, [isDevMode, forgetSession, user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      isAuthenticated: !!token && !!user,
      isLoading,
      devTokenError,
      login,
      startSession,
      signup,
      logout,
      changePassword,
    }),
    [token, user, isLoading, devTokenError, login, startSession, signup, logout, changePassword],
  );

  return (
    <AuthContext.Provider value={value}>
      <QueryClientProvider client={session.queryClient}>
        <React.Fragment key={session.epoch}>{children}</React.Fragment>
      </QueryClientProvider>
    </AuthContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
